import IconArrowLeft from '@apollo/icons/default/IconArrowLeft.svg';
import IconChevronRight from '@apollo/icons/small/IconChevronRight.svg';
import classnames from 'classnames';
import Downshift from 'downshift';
import {
  GraphQLArgument,
  GraphQLField,
  GraphQLNamedType,
  GraphQLSchema,
} from 'graphql';
import React, { Fragment } from 'react';

import { isGraphqlField } from 'src/app/graph/explorerPage/helpers/graphqlHelpers';
import {
  GraphQLPath,
  OperationPath,
  graphQLPathToOperationPath,
} from 'src/app/graph/explorerPage/helpers/operationPathHelpers';
import { ClickableText } from 'src/components/common/clickableText/ClickableText';

import * as SpotlightLayoutComponents from '../spotlightLayoutComponents/SpotlightLayoutComponents';

import { usePathsToType } from './usePathsToType';

type DownshiftItem =
  | {
      groupPath: GraphQLPath;
      path: GraphQLPath;
      endingPath: GraphQLPath;
      isPartial: boolean;
      minimumDistanceToTarget: number;
      index: number;
    }
  | 'return to results';

interface Item {
  groupPath: GraphQLPath;
  path: GraphQLPath;
  isPartial: boolean;
  minimumDistanceToTarget: number;
  endingPath: GraphQLPath;
  isFirstInGroup: boolean;
  groupIndex: number;
}

export const DocsPathSelection = ({
  graphqlSchema,
  fragmentTypeNames,
  parentType,
  field,
  argument,
  onSelect,
  goBackToSearch,
  initialHighlightedIndex,
  onHighlightedIndexChange,
}: {
  graphqlSchema: GraphQLSchema;
  fragmentTypeNames: string[];
  parentType: GraphQLNamedType;
  field: GraphQLField<unknown, unknown>;
  argument?: GraphQLArgument;
  onSelect: (selectedPath: OperationPath | null) => void;
  goBackToSearch: (value?: string) => void;
  initialHighlightedIndex?: number | null | undefined;
  onHighlightedIndexChange?: (index: number | null | undefined) => void;
}) => {
  const searchValue = argument
    ? argument.name
    : `${parentType.name}.${field.name}`;

  const { groupedPaths, expandedPaths, selectPartialPath } = usePathsToType(
    graphqlSchema,
    fragmentTypeNames,
    parentType,
  );

  const isRootSelected = groupedPaths.length !== 1;

  const groupedItems = React.useMemo(
    () =>
      groupedPaths.flatMap((pathsForGroup, i): Item[] =>
        pathsForGroup.paths.map((path, j) => ({
          ...path,
          groupPath: pathsForGroup.groupPath,
          isFirstInGroup: j === 0,
          groupIndex: i,
          endingPath: [field, ...(argument ? [argument] : [])],
        })),
      ),
    [groupedPaths, field, argument],
  );

  // If we have paths, skip the 'Return to results' row, highlight a result.
  const defaultHighlightedIndex = groupedItems.length === 0 ? 0 : 1;
  return (
    <Downshift<DownshiftItem>
      key={(expandedPaths ?? [])
        .map((expandedPath) => expandedPath.map((node) => node.name).join('>'))
        .join(',')}
      itemCount={groupedItems.length + 1}
      defaultHighlightedIndex={defaultHighlightedIndex}
      // We want the input value here to stay what it was in the first search
      // step, we just want the results below to change
      itemToString={() => searchValue}
      onSelect={(item) => {
        if (item === 'return to results') {
          goBackToSearch();
        } else if (item?.isPartial) {
          selectPartialPath([...item.groupPath, ...item.path]);
        } else {
          onSelect(
            item &&
              graphQLPathToOperationPath(
                [...item.groupPath, ...item.path, ...item.endingPath],
                graphqlSchema,
              ),
          );
        }
      }}
      // When the user types in the top bar we should take them back to search
      // with the modified value
      onInputValueChange={(inputValue) => {
        if (inputValue !== searchValue) {
          goBackToSearch(inputValue);
        }
      }}
      initialIsOpen={true}
      initialInputValue={searchValue}
      initialHighlightedIndex={initialHighlightedIndex}
      onStateChange={({ highlightedIndex }) => {
        onHighlightedIndexChange?.(highlightedIndex);
      }}
    >
      {({
        getRootProps,
        getInputProps,
        getItemProps,
        getMenuProps,
        highlightedIndex,
      }) => {
        const highlightIndexOrDefault =
          highlightedIndex ?? defaultHighlightedIndex;
        const highlightedSearchResult =
          groupedItems.length !== 0 &&
          highlightIndexOrDefault !== 0 &&
          groupedItems[highlightIndexOrDefault - 1];
        const fullPath = highlightedSearchResult && [
          ...highlightedSearchResult.groupPath,
          ...highlightedSearchResult.path,
          ...highlightedSearchResult.endingPath,
        ];
        return (
          <SpotlightLayoutComponents.Container {...getRootProps()}>
            <SpotlightLayoutComponents.SearchInput
              {...getInputProps({
                autoFocus: true,
              })}
              onBlur={(e) => {
                // prevent downshift from resetting the state on blur
                // https://github.com/downshift-js/downshift/issues/333#issuecomment-366398528
                e.preventDefault();
              }}
              spellCheck={false}
            />
            <SpotlightLayoutComponents.SearchIcon />
            <div
              className="flex"
              style={{ minHeight: '50vh', maxHeight: '70vh' }}
            >
              <SpotlightLayoutComponents.LeftPanelContainer {...getMenuProps()}>
                <div
                  {...getItemProps({
                    key: 'return-to-results',
                    index: 0,
                    item: 'return to results',
                  })}
                  className={classnames(
                    'mx-3 mt-2 flex cursor-pointer items-center truncate rounded px-3 py-1 text-sm',
                    highlightedIndex === 0
                      ? 'bg-blue text-white'
                      : 'text-secondary',
                  )}
                >
                  <IconArrowLeft />
                  <span className="ml-2">Return to results</span>
                </div>
                {groupedItems.length === 0 ? (
                  <div className="flex-1 py-4 pl-6 pr-2 text-base italic text-secondary">
                    No paths found
                  </div>
                ) : (
                  <ul
                    className="grow overflow-y-auto pb-2"
                    style={{ maxHeight: '50vh' }}
                  >
                    <span className="pl-6 text-sm font-semibold" key="header">
                      Show in documentation at:
                    </span>
                    {groupedItems.map((groupedItem, index) => {
                      const {
                        groupPath,
                        path,
                        endingPath,
                        isPartial,
                        minimumDistanceToTarget,
                        isFirstInGroup,
                      } = groupedItem;
                      const isHighlighted =
                        highlightIndexOrDefault - 1 === index;
                      return (
                        <Fragment
                          key={[...groupPath, ...path, ...endingPath]
                            .map((node) => node.name)
                            .join('>')}
                        >
                          {isFirstInGroup && (
                            <div className="flex flex-wrap items-center px-6 pt-2 font-mono text-sm text-secondary">
                              {[{ name: 'Root' }, ...groupPath].map(
                                (node, i, allNodes) => {
                                  const isRoot = i === 0;
                                  const isLast = i === allNodes.length - 1;
                                  // Root is clickable if not already selected
                                  // Final element is clickable if root selected
                                  // Middle elements are always clickable
                                  const isClickable = isRoot
                                    ? !isRootSelected
                                    : isLast
                                      ? isRootSelected
                                      : true;
                                  // Middle elements always clickable
                                  return (
                                    <Fragment
                                      key={groupPath
                                        .slice(0, i)
                                        .map((n) => n.name)
                                        .join('|')}
                                    >
                                      {isClickable ? (
                                        <ClickableText
                                          className={classnames({
                                            'ml-2': i !== 0,
                                          })}
                                          onClick={() =>
                                            selectPartialPath(
                                              groupPath.slice(0, i),
                                            )
                                          }
                                        >
                                          {node.name}
                                        </ClickableText>
                                      ) : (
                                        <span
                                          className={classnames('text-sm', {
                                            'ml-2': i !== 0,
                                          })}
                                        >
                                          {node.name}
                                        </span>
                                      )}
                                      <IconChevronRight className="ml-2" />
                                    </Fragment>
                                  );
                                },
                              )}
                              <span className="ml-2">…</span>
                            </div>
                          )}
                          <div
                            {...getItemProps({
                              key: path.map((node) => node.name).join('>'),
                              index: index + 1,
                              item: {
                                groupPath,
                                path,
                                endingPath,
                                isPartial,
                                minimumDistanceToTarget,
                                index,
                              },
                            })}
                          >
                            <li
                              className={classnames(
                                'mx-3 flex cursor-pointer flex-wrap items-center rounded py-1 pl-6 pr-3',
                                isHighlighted && 'bg-blue',
                              )}
                            >
                              {/* Ignore the root and operation nodes */}
                              {path.map((node, i) => (
                                <Fragment
                                  key={path
                                    .slice(0, i + 1)
                                    .map((n) => n.name)
                                    .join('|')}
                                >
                                  <span
                                    className={classnames(
                                      'font-mono text-sm',
                                      isHighlighted
                                        ? 'text-white'
                                        : 'text-primary',
                                      { 'ml-2': i !== 0 },
                                    )}
                                  >
                                    {isGraphqlField(node)
                                      ? node.name
                                      : `(${node.name})`}
                                  </span>
                                  <IconChevronRight className="ml-2" />
                                </Fragment>
                              ))}
                              {isPartial && (
                                <Fragment key="showMore">
                                  {Array.from(
                                    // Always show at least one '> … >'
                                    Array(minimumDistanceToTarget || 1).keys(),
                                  ).map((i) => (
                                    <React.Fragment key={i}>
                                      <span
                                        className={classnames(
                                          'ml-1 font-mono text-sm',
                                          isHighlighted
                                            ? 'text-white'
                                            : 'text-secondary',
                                        )}
                                      >
                                        …
                                      </span>
                                      <IconChevronRight className="ml-1" />
                                    </React.Fragment>
                                  ))}
                                </Fragment>
                              )}
                              {endingPath.map((node, i) => (
                                <Fragment
                                  key={endingPath
                                    .slice(0, i + 1)
                                    .map((n) => n.name)
                                    .join('|')}
                                >
                                  <span
                                    className={classnames(
                                      'font-mono text-sm',
                                      isHighlighted
                                        ? 'text-white'
                                        : i === endingPath.length - 1
                                          ? 'text-secondary'
                                          : 'text-primary',
                                      !isGraphqlField(node) && 'italic',
                                      { 'ml-2': i !== 0 || !!path.length },
                                    )}
                                  >
                                    {isGraphqlField(node)
                                      ? node.name
                                      : `(${node.name})`}
                                  </span>
                                  {i !== endingPath.length - 1 && (
                                    <IconChevronRight className="ml-2" />
                                  )}
                                </Fragment>
                              ))}
                            </li>
                          </div>
                        </Fragment>
                      );
                    })}
                  </ul>
                )}
                <SpotlightLayoutComponents.InfoBar />
              </SpotlightLayoutComponents.LeftPanelContainer>
              <SpotlightLayoutComponents.RightPanelContainer>
                {highlightedSearchResult && fullPath ? (
                  <div className="p-6">
                    <div className="text-sm font-semibold">Full path:</div>
                    <div className="mt-2 flex flex-wrap items-center font-mono text-sm">
                      {fullPath
                        .map((pathSegment) => pathSegment.name)
                        .map((name, i, arr) => (
                          <Fragment key={arr.slice(0, i + 1).join('|')}>
                            {i !== 0 && <IconChevronRight className="mx-2" />}
                            <span className="text-primary">
                              {name === 'root' ? 'Root' : name}
                            </span>
                            {highlightedSearchResult.isPartial &&
                              i ===
                                arr.length -
                                  highlightedSearchResult.endingPath.length -
                                  1 && (
                                <>
                                  <IconChevronRight className="mx-2" />
                                  <span className="text-primary">…</span>
                                </>
                              )}
                          </Fragment>
                        ))}
                    </div>
                  </div>
                ) : (
                  <SpotlightLayoutComponents.EmptyState>
                    {groupedItems.length === 0
                      ? 'No paths found to node'
                      : 'Select a path from the results to see it in full'}
                  </SpotlightLayoutComponents.EmptyState>
                )}
              </SpotlightLayoutComponents.RightPanelContainer>
            </div>
          </SpotlightLayoutComponents.Container>
        );
      }}
    </Downshift>
  );
};
